// Comment-pin overlay — the Phase A reviewer surface. Mounted (via the
// DiffBoard `overlay` seam) inside the board's coordinate host, on top of the
// diagram diff. It measures the rects of `[data-module-id]` nodes (the exact
// mechanism GhostLinks uses), drops a pin on every node carrying comments, and
// — in "pin mode" — turns a click on a node into a new node-anchored comment.
//
// Pins track pan/zoom by re-measuring on an animation frame while mounted; the
// layer itself never touches board props, so board memoization is unaffected.
import { FormEvent, useEffect, useRef, useState } from "react";
import { stableId } from "@archlang/engine";
import { useDiffLookup } from "@archlang/app-shell/board";
import { Button } from "../ui/Button.js";
import { CommentThread } from "./CommentThread.js";
import { useReviewThreads } from "./useReviewThreads.js";
import { placePins, nodeCommentOutdated, type RectLike } from "./nodeAnchors.js";
import { useTranslation } from "../i18n";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";

interface CreateAt {
  readonly nodeId: string;
  readonly nodeKind: string;
  readonly x: number;
  readonly y: number;
}

/** Measure host-relative rects of every anchorable node, keyed by StableId. */
function measure(host: HTMLElement): Map<string, RectLike> {
  const out = new Map<string, RectLike>();
  const hostRect = host.getBoundingClientRect();
  host.querySelectorAll<HTMLElement>("[data-module-id]").forEach((el) => {
    if (el.closest("[data-board-context]")) return; // dimmed context copies
    const raw = el.dataset.moduleId;
    if (!raw) return;
    const r = el.getBoundingClientRect();
    // Keep the first (focal) occurrence per id — the split diff renders a node
    // in both panes; the pin lands on whichever measures first.
    const id = stableId(raw);
    if (out.has(id)) return;
    out.set(id, { left: r.left - hostRect.left, top: r.top - hostRect.top, width: r.width, height: r.height });
  });
  return out;
}

function signature(m: ReadonlyMap<string, RectLike>): string {
  const parts: string[] = [];
  for (const [id, r] of m) {
    parts.push(`${id}:${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}`);
  }
  return parts.sort().join("|");
}

export function CommentPinOverlay({
  slug,
  repo,
  proposalId,
  pinMode = false,
  onExitPinMode,
  pendingNodeId = null,
  onConsumePending,
}: {
  readonly slug: string;
  readonly repo: string;
  readonly proposalId: string;
  readonly pinMode?: boolean;
  readonly onExitPinMode?: () => void;
  /** A node the user asked to comment on ("Comment here" context menu) — once
   *  its rect is measured, open the composer there directly (no second click). */
  readonly pendingNodeId?: string | null;
  /** Called when a `pendingNodeId` has been honored (clears the request). */
  readonly onConsumePending?: () => void;
}) {
  const { t } = useTranslation("proposals");
  const review = useReviewThreads(slug, repo, proposalId);
  const lookup = useDiffLookup();
  const menu = useContextMenu();
  const rootRef = useRef<HTMLDivElement>(null);
  const [rects, setRects] = useState<Map<string, RectLike>>(new Map());
  const [openNodeId, setOpenNodeId] = useState<string | null>(null);
  const [createAt, setCreateAt] = useState<CreateAt | null>(null);

  // Re-measure node rects every frame while mounted so pins follow pan/zoom;
  // only commit to state when the measured layout actually changes.
  useEffect(() => {
    let raf = 0;
    let sig = "";
    const tick = () => {
      const host = rootRef.current?.parentElement;
      if (host) {
        const next = measure(host);
        const nextSig = signature(next);
        if (nextSig !== sig) {
          sig = nextSig;
          setRects(next);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // "Comment here" on a specific node: once that node's rect is measured, open
  // the composer at it (mirrors an in-board click) and clear the request. If the
  // node isn't on the diff board its rect never appears — pin mode stays on so a
  // manual click still works, and the request is dropped on tab unmount.
  useEffect(() => {
    if (!pendingNodeId) return;
    const id = stableId(pendingNodeId);
    const r = rects.get(id);
    if (!r) return;
    setCreateAt({ nodeId: id, nodeKind: "module", x: r.left + r.width - 6, y: r.top - 6 });
    onConsumePending?.();
  }, [pendingNodeId, rects, onConsumePending]);

  const roots = review.threads?.comments ?? [];
  const pins = placePins(rects, roots);
  const openPin = pins.find((p) => p.nodeId === openNodeId) ?? null;

  function onLayerClick(e: React.MouseEvent) {
    if (!pinMode) return;
    // Find the node under the pointer (the layer sits above the board in pin mode).
    const el = document
      .elementsFromPoint(e.clientX, e.clientY)
      .find((n): n is HTMLElement => n instanceof HTMLElement && !!n.dataset.moduleId && !n.closest("[data-board-context]"));
    const host = rootRef.current?.parentElement;
    if (!el || !host) return;
    const hostRect = host.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setCreateAt({
      nodeId: stableId(el.dataset.moduleId!),
      nodeKind: "module",
      x: r.left + r.width - hostRect.left - 6,
      y: r.top - hostRect.top - 6,
    });
  }

  function submitCreate(body: string) {
    if (!createAt) return;
    review.postComment(body, {
      anchor: { kind: "node", nodeId: createAt.nodeId, nodeKind: createAt.nodeKind },
    });
    setCreateAt(null);
    onExitPinMode?.();
  }

  return (
    <div
      ref={rootRef}
      onClick={onLayerClick}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: pinMode ? "auto" : "none",
        cursor: pinMode ? "crosshair" : undefined,
      }}
    >
      {pins.map((p) => {
        const outdated = p.comments.some((c) => nodeCommentOutdated(lookup, c));
        const pinMenu: ContextMenuItem[] = [
          { label: t("pins.menuOpen"), onClick: () => setOpenNodeId(p.nodeId) },
          { label: t("panel.copyNodeId"), onClick: () => void navigator.clipboard?.writeText(p.nodeId) },
        ];
        return (
          <button
            key={p.nodeId}
            type="button"
            aria-label={t("pins.commentCount", { count: p.comments.length })}
            onClick={(e) => {
              e.stopPropagation();
              setOpenNodeId((id) => (id === p.nodeId ? null : p.nodeId));
            }}
            onContextMenu={(e) => {
              e.stopPropagation();
              menu.open(e, pinMenu);
            }}
            style={{ position: "absolute", left: p.x, top: p.y, pointerEvents: "auto" }}
            className={`flex h-5 min-w-5 items-center justify-center rounded-full border px-1 text-[11px] font-semibold shadow ${
              outdated
                ? "border-amber-500/60 bg-amber-500/25 text-amber-100"
                : "border-sky-500/60 bg-sky-500/30 text-sky-50"
            }`}
          >
            {p.comments.length}
          </button>
        );
      })}

      {openPin && (
        <div
          style={{ position: "absolute", left: Math.max(0, openPin.x - 300), top: openPin.y + 18, pointerEvents: "auto" }}
          className="w-80 space-y-2 rounded-md border border-slate-700 bg-slate-900 p-2 shadow-xl"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">{t("pins.nodeComments")}</span>
            <button type="button" className="text-xs text-slate-500 hover:text-slate-200" onClick={() => setOpenNodeId(null)}>
              {t("pins.close")}
            </button>
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {openPin.comments.map((c) => (
              <CommentThread
                key={c.id}
                comment={c}
                myId={review.myId}
                busy={review.busy}
                outdated={nodeCommentOutdated(lookup, c)}
                handlers={review.handlers}
              />
            ))}
          </div>
        </div>
      )}

      {createAt && (
        <NodeComposer
          x={createAt.x}
          y={createAt.y}
          disabled={review.busy}
          onCancel={() => setCreateAt(null)}
          onSubmit={submitCreate}
        />
      )}

      {menu.node}
    </div>
  );
}

function NodeComposer({
  x,
  y,
  disabled,
  onSubmit,
  onCancel,
}: {
  readonly x: number;
  readonly y: number;
  readonly disabled?: boolean;
  readonly onSubmit: (body: string) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation("proposals");
  const [draft, setDraft] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (body) onSubmit(body);
  }
  return (
    <form
      onSubmit={submit}
      onClick={(e) => e.stopPropagation()}
      style={{ position: "absolute", left: Math.max(0, x - 260), top: y + 18, pointerEvents: "auto" }}
      className="w-72 rounded-md border border-slate-700 bg-slate-900 p-2 shadow-xl"
    >
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        autoFocus
        placeholder={t("pins.composerPlaceholder")}
        aria-label={t("pins.composerAria")}
        className="w-full resize-none rounded-md border border-slate-700 bg-slate-950 p-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className="text-xs text-slate-400 hover:text-slate-200" onClick={onCancel}>
          {t("pins.cancel")}
        </button>
        <Button type="submit" variant="primary" size="sm" disabled={!draft.trim() || disabled}>
          {t("pins.submit")}
        </Button>
      </div>
    </form>
  );
}
